/**
 * URL Management Helper for Filter Module
 *
 * This file contains all URL and query parameter management utilities for the filter system.
 * It handles URL construction, query parameter manipulation, history management, and URL shortening schemes.
 *
 * Responsibilities:
 * - Query parameter get/set/delete operations
 * - URL history management (push/replace state)
 * - URL shortening and parameter mapping
 * - Multi-value parameter handling with custom separators
 *
 * @module filter.url.helper
 */

import { Injectable } from 'framework/di';

import { FILTER_KEY_PREFIX } from './filter.constants';
import type { FilterAPIParams } from './filter.model';
import { FilterStore } from './filter.store';

/**
 * URL Management Helper for Filter Module
 *
 * Manages all URL and query parameter operations for the filter system, including
 * parameter get/set/delete operations, history management, and URL shortening schemes.
 *
 * This helper can be extended by Technical Support teams to customize URL handling
 * for specific shops, including custom parameter formats, separators, and history behavior.
 *
 * @remarks
 * Supports three URL schemes for parameter handling:
 * - Scheme 0: Standard URLSearchParams behavior (multiple parameters with same key)
 * - Scheme 1: Delete then append all values (ensures clean parameter lists)
 * - Scheme 2: Combine values with custom separator (uses sessionStorage, default)
 *
 * The helper maintains performance by minimizing URL parsing operations and using
 * efficient string manipulation. Protected methods allow TS teams to customize
 * specific behaviors without reimplementing core logic.
 *
 * @example
 * Extend to customize URL shortening:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterUrlHelper', (FilterUrlHelper) => {
 *   return class CustomFilterUrlHelper extends FilterUrlHelper {
 *     protected getShortParamKey(key) {
 *       // Use shop-specific shortening scheme
 *       const customMap = {
 *         'pf_brand': 'brand',
 *         'pf_color': 'color',
 *         'pf_size': 'size'
 *       };
 *       return customMap[key] || super.getShortParamKey(key);
 *     }
 *   };
 * });
 * ```
 *
 * @example
 * Override to use custom parameter separator:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterUrlHelper', (FilterUrlHelper) => {
 *   return class extends FilterUrlHelper {
 *     protected getMultipleValueSeparator() {
 *       return '|'; // Use pipe instead of comma
 *     }
 *   };
 * });
 * ```
 *
 * @example
 * Add analytics tracking on history changes:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterUrlHelper', (FilterUrlHelper) => {
 *   return class extends FilterUrlHelper {
 *     protected mutateHistory(behavior, urlHref, state) {
 *       // Track filter changes
 *       if (window.gtag) {
 *         window.gtag('event', 'filter_change', { url: urlHref });
 *       }
 *       super.mutateHistory(behavior, urlHref, state);
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class FilterUrlHelper {
  private separatorCache: string | null = null;

  constructor(private store: FilterStore) {}

  /**
   * Builds a URLSearchParams object from filter API parameters.
   *
   * Converts a FilterAPIParams object into URLSearchParams format, handling both
   * single values and arrays. Array values are appended with `[]` suffix.
   *
   * @param params - The filter parameters to convert. Keys should be filter parameter
   * names, values can be strings, numbers, booleans, or arrays. Undefined values are skipped.
   *
   * @returns URLSearchParams object with sorted parameters ready for URL construction
   *
   * @example
   * Build query params from filter data:
   * ```typescript
   * const params = {
   *   'pf_brand': ['Nike', 'Adidas'],
   *   'pf_price': '0-100',
   *   'sort': 'price-asc'
   * };
   * const queryParams = this.buildQueryParams(params);
   * // Results in: pf_brand[]=Nike&pf_brand[]=Adidas&pf_price=0-100&sort=price-asc
   * ```
   */
  buildQueryParams(params: FilterAPIParams): URLSearchParams {
    const queryParams = new URLSearchParams();
    queryParams.sort();
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        const value = params[key];
        if (value === undefined) continue;
        if (Array.isArray(value)) {
          value.forEach((item) => {
            queryParams.append(`${key}[]`, item);
          });
        } else {
          queryParams.set(key, String(value));
        }
      }
    }

    return queryParams;
  }

  /**
   * Deletes a query parameter from the URL history.
   *
   * Handles both shortened and full parameter names with multiple URL schemes.
   * For URL scheme 2, also clears associated sessionStorage data.
   *
   * @remarks
   * When using URL shortening (scheme 1 or 2), automatically maps long parameter
   * names to their shortened versions before deletion. For scheme 2, ensures both
   * the URL parameter and sessionStorage entry are removed to prevent data leakage.
   *
   * @param key - The query parameter key to delete (long form, e.g., 'pf_brand').
   * Will be automatically mapped to short key if URL shortening is enabled.
   *
   * @param replace - Whether to replace current history state instead of pushing new state.
   * Default: false (pushes new history entry).
   *
   * @param options - Configuration options for URL handling
   * @param options.preventPushHistory - If true, skip updating browser history entirely. Default: false
   * @param options.isShortenUrlParam - If true, use shortened parameter names. Default: false
   * @param options.urlScheme - URL scheme to use (0=standard, 1=delete+append, 2=combined). Default: 2
   *
   * @example
   * Delete a filter parameter:
   * ```typescript
   * this.urlHelper.deleteQueryParamHistory('pf_brand');
   * // Removes pf_brand from URL and history
   * ```
   *
   * @example
   * Delete with shortened params:
   * ```typescript
   * this.urlHelper.deleteQueryParamHistory('pf_brand', false, {
   *   isShortenUrlParam: true,
   *   urlScheme: 2
   * });
   * // Removes short key 'b' from URL and sessionStorage
   * ```
   */
  deleteQueryParamHistory(
    key: string,
    options: {
      preventPushHistory?: boolean;
      isShortenUrlParam?: boolean;
      urlScheme?: number;
    } = {}
  ): void {
    this.deleteQueryParamsHistory([key], options);
  }

  deleteQueryParamsHistory(
    keys: Array<string>,
    options: {
      preventPushHistory?: boolean;
      isShortenUrlParam?: boolean;
      urlScheme?: number;
    } = {}
  ): void {
    const { preventPushHistory = false, isShortenUrlParam = false, urlScheme = 2 } = options;

    const url = new URL(window.location.href);

    keys.forEach((fullKey) => {
      // Map to short key if enabled
      let paramKey = fullKey;
      if (isShortenUrlParam && urlScheme !== 0) {
        const shortKey = this.getShortParamKey(paramKey);
        if (shortKey) {
          paramKey = shortKey;
        }
      }
      url.searchParams.delete(paramKey);

      if (isShortenUrlParam && urlScheme === 2) {
        this.removeShortenedParam(url, paramKey);
        sessionStorage.removeItem(fullKey);
      }
    });

    if (!isShortenUrlParam || urlScheme !== 2) {
      url.searchParams.sort();
    }

    const behavior = preventPushHistory ? 'replace' : 'push';
    this.mutateHistory(behavior, url.href, {});
  }

  /**
   * Sets a query parameter in the URL history.
   *
   * Handles both shortened and full parameter names with multiple URL schemes.
   * The behavior varies based on the URL scheme configuration.
   *
   * @remarks
   * URL scheme behaviors:
   * - Scheme 0: Uses standard URLSearchParams.set() for single values or multiple append() for arrays
   * - Scheme 1: Always deletes existing parameter then appends all new values
   * - Scheme 2: Combines multiple values with separator, stores full list in sessionStorage
   *
   * Scheme 2 is recommended for cleaner URLs with many filter values. It reduces URL length
   * by combining values like `pf_brand=Nike,Adidas` instead of `pf_brand=Nike&pf_brand=Adidas`.
   *
   * @param key - The query parameter key (long form, e.g., 'pf_brand').
   * Will be automatically mapped to short key if URL shortening is enabled.
   *
   * @param value - The value(s) to set. Can be a single string or array of strings.
   * Arrays are handled differently based on URL scheme.
   *
   * @param replace - Whether to replace current history state instead of pushing new state.
   * Default: false (pushes new history entry).
   *
   * @param options - Configuration options for history and URL handling
   * @param options.preventPushHistory - If true, skip updating browser history. Default: false
   * @param options.isShortenUrlParam - If true, use shortened parameter names. Default: false
   * @param options.urlScheme - URL scheme to use (0=standard, 1=delete+append, 2=combined). Default: 2
   *
   * @example
   * Set a single filter value:
   * ```typescript
   * this.urlHelper.setQueryParamHistory('pf_brand', 'Nike');
   * // URL becomes: ?pf_brand=Nike
   * ```
   *
   * @example
   * Set multiple values with scheme 2:
   * ```typescript
   * this.urlHelper.setQueryParamHistory('pf_color', ['Red', 'Blue'], false, {
   *   isShortenUrlParam: true,
   *   urlScheme: 2
   * });
   * // URL becomes: ?c=Red,Blue (assuming 'c' is short key for pf_color)
   * // sessionStorage also stores: ['Red', 'Blue']
   * ```
   *
   * @example
   * Extend to add validation:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterUrlHelper', (FilterUrlHelper) => {
   *   return class extends FilterUrlHelper {
   *     setQueryParamHistory(key, value, replace, options) {
   *       // Validate filter values before setting
   *       if (key === 'pf_price') {
   *         const valid = this.validatePriceRange(value);
   *         if (!valid) return;
   *       }
   *       super.setQueryParamHistory(key, value, replace, options);
   *     }
   *   };
   * });
   * ```
   */
  setQueryParamHistory(
    key: string,
    value: string | string[],
    replace = false,
    options: {
      preventPushHistory?: boolean;
      isShortenUrlParam?: boolean;
      urlScheme?: number;
    } = {}
  ): void {
    const { preventPushHistory = false, isShortenUrlParam = false, urlScheme = 2 } = options;

    // Map to short key if enabled
    let paramKey = key;
    if (isShortenUrlParam && urlScheme !== 0) {
      const shortKey = this.getShortParamKey(key);
      if (shortKey) {
        paramKey = shortKey;
      }
    }

    const url = new URL(window.location.href);
    const { searchParams } = url;

    if (!isShortenUrlParam) {
      // Standard parameter setting - delete then add new value(s)
      searchParams.delete(paramKey);
      this.applyParamOperation(url, paramKey, value);
    } else {
      if (urlScheme === 0) {
        // Scheme 0: Standard behavior
        this.applyParamOperation(url, paramKey, value);
      } else if (urlScheme === 1) {
        // Scheme 1: Delete then append all values
        url.searchParams.delete(paramKey);
        this.applyParamOperation(url, paramKey, value, { forceAppend: true });
      } else if (urlScheme === 2) {
        // Scheme 2: Combine values with separator
        if (!searchParams.has(paramKey)) {
          sessionStorage.removeItem(key);
        }
        const list = Array.isArray(value) ? value : [value];
        sessionStorage.setItem(key, JSON.stringify(list));
        this.setShortenedParam(url, paramKey, list);
      }
    }

    // Sort params if not using shortened scheme 2
    if (!isShortenUrlParam || urlScheme !== 2) {
      url.searchParams.sort();
    }

    // Update browser history
    if (replace) {
      window.history.replaceState({}, '', url.href);
      return;
    }
    if (!preventPushHistory) {
      window.history.pushState({}, '', url.href);
    }
  }

  /**
   * Appends a value to a query parameter in the URL.
   *
   * Used for multiple selection filters to add new values without replacing existing ones.
   * This is different from setQueryParamHistory which replaces all values.
   *
   * @remarks
   * For URL scheme 2, checks sessionStorage for existing values and appends the new value
   * to the list, preventing duplicates. For other schemes, uses standard URLSearchParams.append().
   *
   * @param key - The query parameter key (long form, e.g., 'pf_brand').
   * Will be automatically mapped to short key if URL shortening is enabled.
   *
   * @param value - The single value to append. Must be a string.
   *
   * @param replace - Whether to replace current history state instead of pushing new state.
   * Default: false (pushes new history entry).
   *
   * @param options - Configuration options for history and URL handling
   * @param options.preventPushHistory - If true, skip updating browser history. Default: false
   * @param options.isShortenUrlParam - If true, use shortened parameter names. Default: false
   * @param options.urlScheme - URL scheme to use (0=standard, 1=delete+append, 2=combined). Default: 2
   *
   * @example
   * Append a brand to existing selection:
   * ```typescript
   * // Current URL: ?pf_brand=Nike
   * this.urlHelper.appendQueryParamHistory('pf_brand', 'Adidas');
   * // URL becomes: ?pf_brand=Nike&pf_brand=Adidas (scheme 0/1)
   * // or: ?pf_brand=Nike,Adidas (scheme 2)
   * ```
   *
   * @example
   * Extend to limit number of values:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterUrlHelper', (FilterUrlHelper) => {
   *   return class extends FilterUrlHelper {
   *     appendQueryParamHistory(key, value, replace, options) {
   *       const params = this.getParamsHistory();
   *       const existing = params.paramList.find(p => p.key === key);
   *       if (existing && existing.values.length >= 10) {
   *         console.warn('Maximum 10 values allowed');
   *         return;
   *       }
   *       super.appendQueryParamHistory(key, value, replace, options);
   *     }
   *   };
   * });
   * ```
   */
  appendQueryParamHistory(
    key: string,
    value: string,
    replace = false,
    options: {
      preventPushHistory?: boolean;
      isShortenUrlParam?: boolean;
      urlScheme?: number;
    } = {}
  ): void {
    const { preventPushHistory = false, isShortenUrlParam = false, urlScheme = 2 } = options;

    // Map to short key if enabled
    let paramKey = key;
    if (isShortenUrlParam && urlScheme !== 0) {
      const shortKey = this.getShortParamKey(key);
      if (shortKey) {
        paramKey = shortKey;
      }
    }

    const url = new URL(window.location.href);
    const { searchParams } = url;

    if (!isShortenUrlParam) {
      // Standard parameter appending - force append
      this.applyParamOperation(url, paramKey, value, { forceAppend: true });
    } else {
      if (urlScheme === 0 || urlScheme === 1) {
        // Schemes 0 & 1: Force append
        this.applyParamOperation(url, paramKey, value, { forceAppend: true });
      } else if (urlScheme === 2) {
        // Scheme 2: Append to combined values
        if (!searchParams.has(paramKey)) {
          sessionStorage.removeItem(key);
        }
        const existingData = sessionStorage.getItem(key);
        const list = existingData ? JSON.parse(existingData) : [];
        if (!list.includes(value)) {
          list.push(value);
        }
        sessionStorage.setItem(key, JSON.stringify(list));
        this.setShortenedParam(url, paramKey, list);
      }
    }

    // Sort params if not using shortened scheme 2
    if (!isShortenUrlParam || urlScheme !== 2) {
      url.searchParams.sort();
    }

    // Update browser history
    if (replace) {
      window.history.replaceState({}, '', url.href);
      return;
    }
    if (!preventPushHistory) {
      window.history.pushState({}, '', url.href);
    }
  }

  /**
   * Removes a specific value from a query parameter in the URL.
   *
   * This is different from deleteQueryParamHistory which removes the entire parameter.
   * If the parameter has multiple values, only the specified value is removed.
   *
   * @param key - The query parameter key to modify (long form, e.g., 'pf_brand')
   * @param value - The specific value to remove from the parameter
   * @param options - Configuration options for history and URL handling
   * @param options.isShortenUrlParam - If true, use shortened parameter names. Default: false
   * @param options.urlScheme - URL scheme to use (0=standard, 1=delete+append, 2=combined). Default: 2
   * @param options.preventPushHistory - If true, skip updating browser history. Default: false
   *
   * @example
   * Remove one value from multi-value parameter:
   * ```typescript
   * // Current URL: ?pf_brand=Nike,Adidas,Puma
   * this.urlHelper.removeQueryParamHistory('pf_brand', 'Adidas');
   * // URL becomes: ?pf_brand=Nike,Puma
   * ```
   */
  removeQueryParamHistory(
    key: string,
    value: string,
    options: {
      isShortenUrlParam?: boolean;
      urlScheme?: number;
      preventPushHistory?: boolean;
    } = {}
  ): void {
    this.removeQueryParamsHistory([{ key, value }], options);
  }

  /**
   * Removes specific values from multiple query parameters in the URL.
   *
   * Handles both shortened and full parameter names with multiple URL schemes.
   * Efficiently processes multiple parameter removals in a single history operation.
   *
   * @remarks
   * For URL scheme 2, updates sessionStorage for each parameter and rebuilds the
   * combined URL representation. If all values are removed from a parameter,
   * the parameter is completely deleted from both URL and sessionStorage.
   *
   * @param params - Array of {key, value} pairs to remove. Each entry specifies
   * a parameter key (long form) and the specific value to remove from that parameter.
   *
   * @param options - Configuration options for history and URL handling
   * @param options.isShortenUrlParam - If true, use shortened parameter names. Default: false
   * @param options.urlScheme - URL scheme to use (0=standard, 1=delete+append, 2=combined). Default: 2
   * @param options.preventPushHistory - If true, skip updating browser history. Default: false
   *
   * @example
   * Remove multiple values in one operation:
   * ```typescript
   * this.urlHelper.removeQueryParamsHistory([
   *   { key: 'pf_brand', value: 'Nike' },
   *   { key: 'pf_color', value: 'Red' }
   * ]);
   * // Removes Nike from brands and Red from colors
   * ```
   */
  removeQueryParamsHistory(
    params: Array<{ key: string; value: string }>,
    options: {
      isShortenUrlParam?: boolean;
      urlScheme?: number;
      preventPushHistory?: boolean;
    } = {}
  ): void {
    const { isShortenUrlParam = false, urlScheme = 2, preventPushHistory = false } = options;
    const url = new URL(window.location.href);
    const { searchParams } = url;

    params.forEach(({ key, value }) => {
      let paramKey = key;
      if (isShortenUrlParam && urlScheme !== 0) {
        const shortKey = this.getShortParamKey(key);
        if (shortKey) {
          paramKey = shortKey;
        }
      }

      if (!searchParams.has(paramKey)) return;

      if (isShortenUrlParam && urlScheme === 2) {
        // Get current values from sessionStorage
        const stored = sessionStorage.getItem(key);
        let list: string[] = stored ? JSON.parse(stored) : [];

        // Remove the specific value
        list = list.filter((v) => v !== value);

        if (list.length > 0) {
          sessionStorage.setItem(key, JSON.stringify(list));
          this.setShortenedParam(url, paramKey, list);
        } else {
          sessionStorage.removeItem(key);
          this.removeShortenedParam(url, paramKey);
        }
      } else {
        // For non-shortened params or urlScheme 0/1
        const oldVals = searchParams.getAll(paramKey).filter((v) => v !== value);
        searchParams.delete(paramKey);
        oldVals.forEach((v) => searchParams.append(paramKey, v));
      }
    });

    // Sort params if not using shortened scheme 2
    if (!isShortenUrlParam || urlScheme !== 2) {
      const sorted = Array.from(searchParams.entries()).sort(([a], [b]) => a.localeCompare(b));
      const newParams = new URLSearchParams();
      sorted.forEach(([k, v]) => newParams.append(k, v));
      url.search = newParams.toString();
    }

    // Update browser history
    const behavior = preventPushHistory ? 'replace' : 'push';
    this.mutateHistory(behavior, url.href);
  }

  /**
   * Gets a query parameter value by key from current URL.
   *
   * @param key - The query parameter key to retrieve (e.g., 'pf_brand', 'sort')
   * @returns The parameter value or null if not found. For multi-value parameters,
   * returns only the first value.
   *
   * @example
   * Get current sort parameter:
   * ```typescript
   * const sort = this.urlHelper.getQueryParamByKey('sort');
   * // Returns: 'price-asc' or null
   * ```
   */
  getQueryParamByKey(key: string): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(key);
  }

  /**
   * Parses URL search parameters and extracts filter params, sort, and pagination.
   *
   * Handles both shortened and full parameter formats by mapping short keys back
   * to their long form using the longParamsMap from store.
   *
   * @remarks
   * This method handles multiple edge cases for robust URL parsing:
   * - Decodes URL-encoded values and replaces '+' with spaces
   * - Splits multi-value parameters by configured separator (default ',')
   * - Maps shortened parameter keys back to full keys using longParamsMap
   * - Handles both encoded and non-encoded separators in URLs
   * - Preserves parameter order for consistent behavior
   * - Only extracts parameters with the FILTER_KEY_PREFIX ('pf_')
   *
   * @returns Object containing:
   * - paramList: Array of filter parameters, each with key (long form) and values array
   * - sort: Current sort parameter string or null if not present
   * - pagination: Object with page property (string or null)
   *
   * @example
   * Parse current URL parameters:
   * ```typescript
   * const { paramList, sort, pagination } = this.urlHelper.getParamsHistory();
   * // paramList = [{ key: 'pf_brand', values: ['Nike', 'Adidas'] }]
   * // sort = 'price-asc'
   * // pagination = { page: '2' }
   * ```
   *
   * @example
   * Extend to add custom parameter parsing:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterUrlHelper', (FilterUrlHelper) => {
   *   return class extends FilterUrlHelper {
   *     getParamsHistory() {
   *       const result = super.getParamsHistory();
   *       // Add custom parameter extraction
   *       const customParam = new URLSearchParams(window.location.search).get('custom');
   *       return { ...result, customParam };
   *     }
   *   };
   * });
   * ```
   */
  getParamsHistory(): {
    paramList: Array<{ key: string; values: string[] }>;
    sort: string | null;
    pagination: { page: string | null };
  } {
    const raw = window.location.search.replace(/^\?/, '');
    const pairs = raw ? raw.split('&') : [];
    const separator = this.getMultipleValueSeparator();
    const longParamsMap = this.store.getState('longParamsMap') || new Map<string, string>();

    const rawObj: Record<string, string[]> = {};

    for (const p of pairs) {
      const [rawKey, rawVal = ''] = p.split('=');
      let key = decodeURIComponent(rawKey);

      // Map short key to long key if available
      if (longParamsMap.has(key)) {
        key = longParamsMap.get(key)!;
      }

      // Escape separator for regex
      const REGEX_ESCAPE_PATTERN = /[.*+?^${}()|[\]\\]/g;
      const escapedSeparator = separator.replace(REGEX_ESCAPE_PATTERN, '\\$&');
      const encodedSeparator = encodeURIComponent(separator);
      const escapedEncodedSeparator = encodedSeparator.replace(REGEX_ESCAPE_PATTERN, '\\$&');
      const splitRegex = new RegExp(`${escapedSeparator}|${escapedEncodedSeparator}`);

      // Split values by separator and decode
      const segments = rawVal.split(splitRegex);
      const decoded = segments.map((v) => decodeURIComponent(v.replace(/\+/g, ' ')));

      if (!rawObj[key]) {
        rawObj[key] = [];
      }
      rawObj[key].push(...decoded);
    }

    // Extract filter parameters
    const paramList: Array<{ key: string; values: string[] }> = [];
    for (const [k, arr] of Object.entries(rawObj)) {
      if (k.startsWith(FILTER_KEY_PREFIX)) {
        paramList.push({ key: k, values: arr });
      }
    }

    // Extract sort and pagination
    const urlParams = new URLSearchParams(window.location.search);
    const sort = urlParams.get('sort');
    const pagination = { page: urlParams.get('page') };

    return { paramList, sort, pagination };
  }

  /**
   * Rewrites the URL with new filter parameters, sort, and pagination.
   *
   * Clears all existing filter parameters and sets new ones based on provided data.
   * This is a complete URL reconstruction, useful for applying bulk filter changes.
   *
   * @remarks
   * This method performs a complete URL rewrite in several steps:
   * 1. Preserves 'q' (search query) and 'collection_scope' parameters
   * 2. Removes all existing filter parameters (identified by FILTER_KEY_PREFIX)
   * 3. Clears standard parameters (sort, page)
   * 4. Applies new parameters from the data object
   * 5. Uses replaceState to avoid creating new history entry
   *
   * Performance note: For large filter sets, this is more efficient than
   * multiple individual parameter operations.
   *
   * @param data - Object containing complete filter state
   * @param data.paramList - Array of filter parameters with their values
   * @param data.sort - Sort parameter string or null
   * @param data.pagination - Pagination object with page property
   *
   * @param options - Configuration options for URL handling
   * @param options.isShortenUrlParam - If true, use shortened parameter names. Default: false
   * @param options.urlScheme - URL scheme to use (0=standard, 1=delete+append, 2=combined). Default: 2
   *
   * @example
   * Rewrite URL with new filter state:
   * ```typescript
   * this.urlHelper.rewriteUrlFromParams({
   *   paramList: [
   *     { key: 'pf_brand', values: ['Nike'] },
   *     { key: 'pf_color', values: ['Red', 'Blue'] }
   *   ],
   *   sort: 'price-asc',
   *   pagination: { page: '1' }
   * });
   * ```
   */
  rewriteUrlFromParams(
    data: {
      paramList: Array<{ key: string; values: string[] }>;
      sort: string | null;
      pagination: { page: string | null };
    },
    options: {
      isShortenUrlParam?: boolean;
      urlScheme?: number;
    } = {}
  ): void {
    const { paramList, sort, pagination } = data;
    const { isShortenUrlParam = false, urlScheme = 2 } = options;

    const url = new URL(window.location.href);
    const q = url.searchParams.get('q');
    const currentCollection = url.searchParams.get('collection_scope');

    // Get maps for shortened URLs
    const shortParamsMap = this.store.getState('shortParamsMap') || new Map<string, string>();
    const longParamsMap = this.store.getState('longParamsMap') || new Map<string, string>();

    // Collect keys to delete
    const toDelete: string[] = [];
    url.searchParams.forEach((_, key) => {
      if (key === 'sort' || key === 'page' || key === 'q' || key === 'collection_scope') return;
      if (isShortenUrlParam) {
        toDelete.push(key);
      } else {
        if (key.startsWith(FILTER_KEY_PREFIX) || longParamsMap.has(key)) {
          toDelete.push(key);
        }
      }
    });
    toDelete.forEach((k) => url.searchParams.delete(k));

    // Clear standard params
    url.searchParams.delete('sort');
    url.searchParams.delete('page');
    url.searchParams.delete('q');
    url.searchParams.delete('collection_scope');

    // Set standard params if they exist
    if (sort) url.searchParams.set('sort', sort);
    if (pagination.page) url.searchParams.set('page', pagination.page);
    if (q) url.searchParams.set('q', q);
    if (currentCollection) url.searchParams.set('collection_scope', currentCollection);

    // Set filter params
    paramList.forEach(({ key: fullKey, values }) => {
      if (isShortenUrlParam && urlScheme !== 0) {
        const shortKey = shortParamsMap.get(fullKey) || fullKey;

        if (urlScheme === 1) {
          values.forEach((v) => {
            url.searchParams.append(shortKey, v);
          });
        } else {
          this.setShortenedParam(url, shortKey, values);
        }
      } else {
        values.forEach((v) => {
          url.searchParams.append(fullKey, v);
        });
      }
    });

    window.history.replaceState({}, '', url.href);
  }

  /**
   * Clears all filter parameters from URL history.
   *
   * Removes all parameters that start with FILTER_KEY_PREFIX ('pf_') or their
   * shortened equivalents if URL shortening is enabled.
   *
   * @param options - Configuration options for URL handling
   * @param options.preventPushHistory - If true, use replaceState instead of pushState. Default: false
   * @param options.isShortenUrlParam - If true, also clear shortened parameter names. Default: false
   *
   * @example
   * Clear all filters:
   * ```typescript
   * this.urlHelper.clearAllParamHistory();
   * // Removes all pf_* parameters from URL
   * ```
   *
   * @example
   * Clear filters without adding to history:
   * ```typescript
   * this.urlHelper.clearAllParamHistory({ preventPushHistory: true });
   * ```
   */
  clearAllParamHistory(
    options: {
      preventPushHistory?: boolean;
      isShortenUrlParam?: boolean;
    } = {}
  ): void {
    const { preventPushHistory = false, isShortenUrlParam = false } = options;
    const url = new URL(window.location.href);
    const { searchParams } = url;
    const removeKeys: string[] = [];
    const shortParamsMap = this.store.getState('shortParamsMap');

    const validShortKeys = new Set();
    if (isShortenUrlParam) {
      for (const [longKey, shortKey] of shortParamsMap.entries()) {
        if (longKey.startsWith(FILTER_KEY_PREFIX)) {
          validShortKeys.add(shortKey);
        }
      }
    }

    searchParams.forEach((_, key) => {
      if (key.startsWith(FILTER_KEY_PREFIX)) {
        removeKeys.push(key);
      } else if (isShortenUrlParam && validShortKeys.has(key)) {
        removeKeys.push(key);
      }
    });

    removeKeys.forEach((k) => searchParams.delete(k));
    const behavior = preventPushHistory ? 'replace' : 'push';
    this.mutateHistory(behavior, url.href, {});
  }

  /**
   * Checks if filter option parameters exist in current URL.
   *
   * Scans URL parameters for any that start with 'pf_' prefix, mapping shortened
   * keys to long keys if URL shortening is enabled.
   *
   * @param isShortenUrlParam - Whether shortened URL params are enabled. If true,
   * maps short keys to long keys before checking prefix. Default: false
   *
   * @returns true if at least one filter parameter exists in URL, false otherwise
   *
   * @example
   * Check if filters are applied:
   * ```typescript
   * const hasFilters = this.urlHelper.checkExistFilterOptionParam();
   * if (hasFilters) {
   *   this.showClearAllButton();
   * }
   * ```
   */
  checkExistFilterOptionParam(isShortenUrlParam = false): boolean {
    const queryParams = new URLSearchParams(window.location.search);
    const longParamsMap = this.store.getState('longParamsMap');
    for (const [key] of queryParams.entries()) {
      let finalKey = key;

      if (isShortenUrlParam && longParamsMap.has(key)) {
        const longKey = longParamsMap.get(key);
        if (longKey) {
          finalKey = longKey;
        }
      }

      if (finalKey.indexOf('pf_') === 0) {
        return true;
      }
    }

    return false;
  }

  /**
   * Gets the URL scheme configuration.
   *
   * @returns URL scheme number: 0 (standard), 1 (delete+append), or 2 (combined with separator).
   * Default: 2
   *
   * @example
   * Check current URL scheme:
   * ```typescript
   * const scheme = this.urlHelper.getUrlScheme();
   * if (scheme === 2) {
   *   console.log('Using combined parameter format');
   * }
   * ```
   */
  getUrlScheme(): number {
    const config = (
      window as unknown as {
        boostWidgetIntegration?: {
          config?: {
            customization?: {
              filter?: {
                customizedAPIParams?: { urlScheme?: number };
              };
            };
          };
        };
      }
    ).boostWidgetIntegration?.config?.customization?.filter?.customizedAPIParams?.urlScheme;

    return typeof config === 'number' ? config : 2;
  }

  // ============================================
  // Private Helper Methods
  // ============================================

  /**
   * Gets shortened parameter key if URL shortening is enabled.
   *
   * Maps long parameter keys (e.g., 'pf_brand') to their shortened versions
   * (e.g., 'b') using the shortParamsMap from store.
   *
   * Override this method to implement custom URL shortening schemes for specific shops.
   *
   * @param key - Original parameter key (long form, e.g., 'pf_brand')
   * @returns Shortened key (e.g., 'b') or null if no mapping exists
   *
   * @example
   * Custom shortening scheme:
   * ```typescript
   * protected getShortParamKey(key: string): string | null {
   *   // Use shop-specific SEO-friendly keys
   *   const customMap: Record<string, string> = {
   *     'pf_brand': 'brand',
   *     'pf_color': 'color',
   *     'pf_size': 'size',
   *     'pf_price': 'price'
   *   };
   *   return customMap[key] || super.getShortParamKey(key);
   * }
   * ```
   */
  protected getShortParamKey(key: string): string | null {
    const shortParamsMap = this.store.getState('shortParamsMap');
    return shortParamsMap?.get(key) || null;
  }

  /**
   * Applies parameter operation to URL (set or append).
   *
   * Handles both single values and arrays, with optional force-append mode.
   * Override this method to customize how parameters are added to URLs.
   *
   * @param url - URL object to modify in place
   * @param key - The parameter key to set
   * @param value - The value(s) to set. Single string or array of strings
   * @param options - Configuration options
   * @param options.forceAppend - If true, always use append() instead of set(). Default: false
   *
   * @example
   * Add parameter deduplication:
   * ```typescript
   * protected applyParamOperation(
   *   url: URL,
   *   key: string,
   *   value: string | string[],
   *   options: { forceAppend?: boolean } = {}
   * ): void {
   *   // Remove duplicates before applying
   *   const values = Array.isArray(value) ? [...new Set(value)] : [value];
   *   super.applyParamOperation(url, key, values, options);
   * }
   * ```
   */
  protected applyParamOperation(
    url: URL,
    key: string,
    value: string | string[],
    options: { forceAppend?: boolean } = {}
  ): void {
    const { forceAppend = false } = options;

    if (Array.isArray(value)) {
      value.forEach((v) => url.searchParams.append(key, v));
    } else {
      if (forceAppend) {
        url.searchParams.append(key, value);
      } else {
        url.searchParams.set(key, value);
      }
    }
  }

  /**
   * Gets the configured separator for multiple values in a URL parameter.
   *
   * Returns the separator character used to combine multiple filter values in a single
   * URL parameter (URL scheme 2). Override this method to use custom separators for
   * specific shops or regions.
   *
   * The result is cached for performance since this method may be called frequently
   * during URL parsing and construction.
   *
   * @returns The separator character (defaults to ',')
   *
   * @example
   * Use pipe separator:
   * ```typescript
   * protected getMultipleValueSeparator(): string {
   *   return '|'; // Better for URLs with commas in values
   * }
   * ```
   *
   * @example
   * Regional separator based on shop locale:
   * ```typescript
   * protected getMultipleValueSeparator(): string {
   *   const locale = this.store.getState('shopLocale');
   *   return locale === 'de' ? ';' : ',';
   * }
   * ```
   */
  protected getMultipleValueSeparator(): string {
    // Return cached value if available
    if (this.separatorCache !== null) {
      return this.separatorCache;
    }

    // Get from config and cache
    const separator = (
      window as unknown as {
        boostWidgetIntegration?: {
          config?: {
            customization?: {
              filter?: {
                customizedAPIParams?: { multipleValueSeparator?: string };
              };
            };
          };
        };
      }
    ).boostWidgetIntegration?.config?.customization?.filter?.customizedAPIParams
      ?.multipleValueSeparator;

    this.separatorCache = typeof separator === 'string' && separator.length > 0 ? separator : ',';
    return this.separatorCache;
  }

  /**
   * Sets a shortened parameter in URL with combined values.
   *
   * Used for URL scheme 2 to combine multiple values with the configured separator.
   * Override this method to customize how shortened parameters are encoded in URLs.
   *
   * @remarks
   * This method manually constructs the URL search string to maintain precise control
   * over parameter ordering and encoding. It:
   * 1. Encodes values and replaces %20 with + for compatibility
   * 2. Combines values with the configured separator
   * 3. Maintains sorted parameter order for consistent URLs
   *
   * @param url - URL object to modify in place
   * @param key - The shortened parameter key (e.g., 'b' for 'pf_brand')
   * @param rawValues - Array of values to combine with separator
   *
   * @example
   * Custom encoding for special characters:
   * ```typescript
   * protected setShortenedParam(url: URL, key: string, rawValues: string[]): void {
   *   // Use custom encoding for values with special characters
   *   const separator = this.getMultipleValueSeparator();
   *   const encodedValues = rawValues
   *     .map(v => this.customEncode(v))
   *     .join(separator);
   *
   *   const prefix = url.search.startsWith('?') ? url.search.slice(1) : '';
   *   const pairs = prefix ? prefix.split('&') : [];
   *   const filtered = pairs.filter(p => !p.startsWith(encodeURIComponent(key) + '='));
   *   url.search = [...filtered, `${encodeURIComponent(key)}=${encodedValues}`].join('&');
   * }
   * ```
   */
  protected setShortenedParam(url: URL, key: string, rawValues: string[]): void {
    const prefix = url.search.startsWith('?') ? url.search.slice(1) : '';
    const pairs = prefix ? prefix.split('&') : [];
    const separator = this.getMultipleValueSeparator();
    const encodedValues = rawValues
      .map((v) => encodeURIComponent(v).replace(/%20/g, '+'))
      .join(separator);

    const filtered = pairs.filter((p) => !p.startsWith(encodeURIComponent(key) + '='));
    const all = [...filtered, `${encodeURIComponent(key)}=${encodedValues}`];
    all.sort((a, b) => {
      const [rawKeyA] = a.split('=');
      const [rawKeyB] = b.split('=');
      return decodeURIComponent(rawKeyA).localeCompare(decodeURIComponent(rawKeyB));
    });
    url.search = all.length ? `?${all.join('&')}` : '';
  }

  /**
   * Removes a shortened parameter from URL search params.
   *
   * Handles the custom format used by URL scheme 2. Override this method to customize
   * how shortened parameters are removed from URLs.
   *
   * @remarks
   * This method manually parses and reconstructs the URL search string to handle
   * the custom parameter format used by URL scheme 2. It ensures complete removal
   * of the parameter without affecting other parameters.
   *
   * @param url - URL object to modify in place
   * @param key - The shortened parameter key to remove (e.g., 'b' for 'pf_brand')
   *
   * @example
   * Add logging when removing parameters:
   * ```typescript
   * protected removeShortenedParam(url: URL, key: string): void {
   *   console.log(`Removing shortened parameter: ${key}`);
   *   super.removeShortenedParam(url, key);
   * }
   * ```
   */
  protected removeShortenedParam(url: URL, key: string): void {
    const prefix = url.search.startsWith('?') ? url.search.slice(1) : '';
    const pairs = prefix ? prefix.split('&') : [];
    const encKey = encodeURIComponent(key);
    const filtered = pairs.filter((p) => !p.startsWith(encKey + '='));

    url.search = filtered.length ? `?${filtered.join('&')}` : '';
  }

  /**
   * Updates browser history with the given URL.
   *
   * Override this method to add custom behavior like analytics tracking, logging,
   * or state management when URL changes occur.
   *
   * @remarks
   * This is the single point where all URL changes are committed to browser history.
   * Extending this method allows Technical Support teams to:
   * - Track filter changes in analytics platforms
   * - Log URL changes for debugging
   * - Sync URL state with external systems
   * - Add custom state objects to history entries
   *
   * @param behavior - History operation: 'push' for new entry, 'replace' to update current entry
   * @param urlHref - The complete URL string to set
   * @param state - Optional state object to store with history entry. Default: {}
   *
   * @example
   * Add analytics tracking:
   * ```typescript
   * protected mutateHistory(
   *   behavior: 'push' | 'replace',
   *   urlHref: string,
   *   state: object = {}
   * ): void {
   *   // Track filter changes in Google Analytics
   *   if (window.gtag && behavior === 'push') {
   *     const url = new URL(urlHref);
   *     const filters = Array.from(url.searchParams.entries())
   *       .filter(([k]) => k.startsWith('pf_'))
   *       .map(([k, v]) => `${k}:${v}`);
   *
   *     window.gtag('event', 'filter_change', {
   *       filters: filters.join(','),
   *       page_path: url.pathname + url.search
   *     });
   *   }
   *
   *   super.mutateHistory(behavior, urlHref, state);
   * }
   * ```
   *
   * @example
   * Sync with custom state management:
   * ```typescript
   * protected mutateHistory(
   *   behavior: 'push' | 'replace',
   *   urlHref: string,
   *   state: object = {}
   * ): void {
   *   // Store timestamp and user info in history state
   *   const enhancedState = {
   *     ...state,
   *     timestamp: Date.now(),
   *     userId: this.getCurrentUserId()
   *   };
   *
   *   super.mutateHistory(behavior, urlHref, enhancedState);
   * }
   * ```
   */
  protected mutateHistory(behavior: 'push' | 'replace', urlHref: string, state: object = {}): void {
    if (behavior === 'push') {
      window.history.pushState(state, '', urlHref);
    } else {
      window.history.replaceState(state, '', urlHref);
    }
  }
}
